import socketService from '../../services/socketService';
import orderMirror from '../../services/orderMirror';
import offlineManager from '../../services/offlineManager';
import { upsertOrder, removeOrderById, setOrders } from './ordersSlice';
import { upsertTable, setTables } from './tablesSlice';
import { setSelectedBranch, setSelectedRestaurantId } from './branchSlice';
import { socketConnected, socketDisconnected, socketError } from './socketSlice';
import { setMode } from './offlineSlice';
import { loadInitialPOSData } from './thunks';
import { getStoredTokenSync } from '../../utils/authStorage';

export const SOCKET_DISCONNECT   = 'socket/disconnect';
export const SOCKET_REQUEST_SYNC = 'socket/requestSync';

const socketMiddleware = (store) => {
	let listenersSetup = false;
	// Prevent duplicate loadInitialPOSData calls when setSelectedRestaurantId fires
	// multiple times during init while the branch hasn't actually changed.
	let lastLoadedBranchId = null;

	function ensureListeners() {
		if (listenersSetup) return;
		listenersSetup = true;

		socketService.addEventListener('socket_connected', () => {
			console.warn('[socket] connected');
			store.dispatch(socketConnected());
		});
		socketService.addEventListener('socket_disconnected', () => {
			console.warn('[socket] disconnected');
			store.dispatch(socketDisconnected());
		});
		socketService.addEventListener('socket_error', (data) => {
			console.warn('[socket] error', { error: data?.error || null });
			store.dispatch(socketError(data?.error ?? null));
		});

		// On a sync-replay event, the same logical order is now arriving under
		// its new server UUID. Remove the lingering local_*** entry first so the
		// table grid doesn't briefly show two cards for one order while the
		// socket event races the sync HTTP response.
		const stripLocalGhost = (order, isSyncReplay) => {
			if (!isSyncReplay) return;
			const localId = order?.localId || order?.local_id;
			if (localId && localId !== (order._id || order.id)) {
				console.warn('[sync-replay] removing local ghost', {
					localId,
					serverId: order._id || order.id,
				});
				store.dispatch(removeOrderById(localId));
			}
		};

		// Socket order payloads are not guaranteed to be transformOrder-shaped —
		// sync-replay emits historically carried raw snake_case DB rows. The
		// entity store and every consumer (table grid matches orders by
		// camelCase tableId!) read camelCase, so alias the differing keys
		// before upserting. A snake-only create payload replacing a local
		// entity turned its table "available" until the next fetch (branch3,
		// 2026-08-12).
		const SNAKE_TO_CAMEL = {
			table_id: 'tableId', table_number: 'tableNumber', order_type: 'orderType',
			order_number: 'orderNumber', local_id: 'localId',
			payment_method: 'paymentMethod', payment_date: 'paymentDate',
			cash_amount: 'cashAmount', card_amount: 'cardAmount',
			discount_type: 'discountType', discount_value: 'discountValue', discount_amount: 'discountAmount',
			modification_history: 'modificationHistory', current_modification_cycle: 'currentModificationCycle',
			billed_at: 'billedAt', last_billed_at: 'lastBilledAt', last_served_at: 'lastServedAt',
			session_id: 'sessionId', tax_exempt: 'taxExempt', customer_name: 'customerName',
			customer_info: 'customerInfo', special_instructions: 'specialInstructions',
			branch_id: 'branchId', server_id: 'serverId',
			created_at: 'createdAt', updated_at: 'updatedAt', kds_visible: 'kdsVisible',
			client_updated_at: 'clientUpdatedAt',
		};
		const normalizeOrderShape = (order) => {
			if (!order || typeof order !== 'object') return order;
			const out = { ...order };
			for (const [snake, camel] of Object.entries(SNAKE_TO_CAMEL)) {
				if (out[snake] !== undefined && out[camel] === undefined) out[camel] = out[snake];
			}
			return out;
		};

		// ── Freshness guard for local-first devices ──────────────────────────
		// When THIS device holds an un-acked local edit (_isLocal — the pump
		// hasn't settled it yet), a socket emit that isn't provably newer is a
		// stale echo: the server re-broadcasting our own earlier write, or a
		// snapshot that predates the edit. Applying it rolled a just-paid
		// order back to active (table re-occupied) until the next pump round.
		// A genuinely newer remote edit (stamp > local) always passes.
		const stampOf = (o) => {
			const v = o?.clientUpdatedAt || o?.client_updated_at || o?.updatedAt || o?.updated_at;
			const t = v ? Date.parse(v) : NaN;
			return Number.isNaN(t) ? null : t;
		};
		const TERMINAL_STATUSES = ['completed', 'cancelled'];
		// Lifecycle ranks mirror the backend's STATUS_ORDER — used so the guard
		// NEVER drops an emit that moves the order forward.
		const STATUS_RANK = { draft: 0, confirmed: 1, preparing: 2, ready: 3, served: 4, billed: 5, completed: 6, cancelled: 7 };
		const isStaleForLocalEdit = (incoming) => {
			try {
				const id = String(incoming?._id || incoming?.id || '');
				if (!id) return false;
				const entities = store.getState()?.orders?.entities || {};
				const existing = entities[id]
					|| Object.values(entities).find((o) => o?.localId && String(o.localId) === id);
				if (!existing || existing._isLocal !== true) return false;
				// Emit would re-open a locally-terminal order -> always stale,
				// regardless of stamps (this rule is clock-independent).
				if (TERMINAL_STATUSES.includes(existing.status)
					&& incoming?.status && !TERMINAL_STATUSES.includes(incoming.status)) {
					return true;
				}
				// Clock-skew hardening: local updatedAt is THIS device's wall
				// clock; the incoming stamp is the server's (or another
				// device's). They are only roughly comparable, so an emit that
				// ADVANCES the lifecycle — new payment, higher status — must
				// always pass; dropping it could hide a payment made on
				// another device.
				if (incoming?.paid === true && existing.paid !== true) return false;
				const localRank = STATUS_RANK[existing.status] ?? 0;
				const remoteRank = incoming?.status != null ? (STATUS_RANK[incoming.status] ?? 0) : 0;
				if (incoming?.status != null && remoteRank > localRank) return false;
				const localTs = stampOf(existing);
				const remoteTs = stampOf(incoming);
				if (localTs != null && remoteTs != null && remoteTs > localTs) return false;
				// Comparable stamps, remote is not newer, no lifecycle advance
				// -> stale echo of our own un-acked edit.
				if (localTs != null && remoteTs != null) return true;
				// Can't compare -- let it through rather than drop a real edit.
				return false;
			} catch (_) {
				return false;
			}
		};
		const dropIfStale = (payload, event) => {
			if (!isStaleForLocalEdit(payload)) return false;
			console.warn('[socket] dropped stale emit over pending local edit', {
				event, orderId: payload?._id || payload?.id, incomingStatus: payload?.status,
			});
			return true;
		};

		socketService.addEventListener('order_created', (data) => {
			const order = normalizeOrderShape(data?.order ?? data);
			if (!order) return;
			stripLocalGhost(order, data?.syncReplay === true);
			if (dropIfStale(order, 'order_created')) return;
			store.dispatch(upsertOrder(order));
		});
		socketService.addEventListener('order_updated', (data) => {
			const order   = normalizeOrderShape(data?.order ?? data?.orderData);
			const payload = order
				? { ...order, _id: order._id ?? order.id, id: order.id ?? order._id }
				: data?.orderId
					? { _id: data.orderId, id: data.orderId, status: data.status, ...data }
					: null;
			if (!payload) return;
			stripLocalGhost(payload, data?.syncReplay === true);
			if (dropIfStale(payload, 'order_updated')) return;
			store.dispatch(upsertOrder(payload));
		});
		socketService.addEventListener('order_deleted', (data) => {
			if (data?.orderId) {
				// Real deletion from another device: teach the mirror BEFORE the
				// entity leaves Redux — the mirror middleware deliberately ignores
				// removeOrderById (it's usually a view change), so without this a
				// restart would hydrate the deleted order back as an occupied table.
				try {
					const prev = store.getState()?.orders?.entities?.[data.orderId];
					if (prev) orderMirror.mirrorOrders([{ ...prev, status: 'cancelled' }]);
				} catch (_) { /* mirror is best-effort */ }
				store.dispatch(removeOrderById(data.orderId));
			}
		});
		socketService.addEventListener('order_status_changed', (data) => {
			if (data?.orderId) {
				const payload = normalizeOrderShape({ _id: data.orderId, id: data.orderId, status: data.status, ...data });
				if (dropIfStale(payload, 'order_status_changed')) return;
				store.dispatch(upsertOrder(payload));
			}
		});
		socketService.addEventListener('table_status_changed', (data) => {
			const tableId = data?.tableId;
			if (tableId) {
				store.dispatch(upsertTable({ _id: tableId, id: tableId, status: data.status, ...data }));
			}
		});
		socketService.addEventListener('sync_orders', (data) => {
			if (data?.orders) store.dispatch(setOrders(data.orders));
		});
		socketService.addEventListener('sync_tables', (data) => {
			if (data?.tables) store.dispatch(setTables(data.tables));
		});
	}

	return (next) => (action) => {
		const result = next(action);

		if (action.type === setSelectedBranch.type || action.type === setSelectedRestaurantId.type) {
			const state    = store.getState();
			const branchId = state.branch.selectedBranchId;

			if (branchId) {
				// Guard: never fire API calls when there is no auth token.
				// BranchContext restores selectedBranch from localStorage on every mount
				// (including when the sign-in page loads after a session expiry), which
				// dispatches setSelectedBranch and would otherwise trigger loadInitialPOSData
				// with no token → flood of 401s.
				const token = getStoredTokenSync();
				if (!token) return result;

				// Initialise offlineManager with store + branchId
				offlineManager.init(store, branchId);

				// Only connect socket when in Online mode
				const mode = offlineManager.getMode();
				if (mode === 'online') {
					ensureListeners();
					socketService.connect(branchId);
				}

				// Only fire the initial data load when the branch actually changes.
				// setSelectedRestaurantId fires 2-3× during app init (restaurant context
				// dispatches it on mount, then again after the restaurant list loads from
				// the API) but the branchId hasn't changed — so skip the redundant loads.
				if (branchId !== lastLoadedBranchId) {
					lastLoadedBranchId = branchId;
					store.dispatch(loadInitialPOSData({ branchId }));
				}
			}
		}

		if (action.type === setMode.type) {
			const state = store.getState();
			const branchId = state.branch.selectedBranchId;
			const token = getStoredTokenSync();
			if (branchId && token) {
				if (action.payload === 'online') {
					ensureListeners();
					socketService.connect(branchId);
				} else {
					socketService.registerPrintStation(branchId);
					socketService.disconnect();
				}
			}
		}

		if (action.type === SOCKET_DISCONNECT) {
			socketService.disconnect();
		}

		if (action.type === SOCKET_REQUEST_SYNC) {
			socketService.requestSync();
		}

		return result;
	};
};

export default socketMiddleware;
